import { useEffect, useMemo, useState } from "react";
import {
  Box,
  VStack,
  Image,
  Text,
  Stack,
  HStack,
  CircularProgress,
  //   useDisclosure,
  useToast,
} from "@chakra-ui/react";

import { useHistory } from "react-router-dom";

import Button from "components/Button/Button";
import Card, { getCardScheme } from "components/Card/Card";
import EmptyCards from "assets/png/empty-cards.png";
import { ReactComponent as AddCircleIcon } from "assets/svg/add-circle.svg";
import { useUnfreezeUserCardMutation } from "services/cardsApi";

import toLower from "lodash/toLower";
import getError from "util/getError";
import { capitalize } from "lodash";
import Select from "components/Select/Select";

const EmptyState = ({ isEligible, cards }) => {
  const history = useHistory();
  return (
    <VStack mt="60px">
      <Image boxSize="180px" objectFit="contain" src={EmptyCards} />
      <Text color="#6E7191" mt="0 !important">
        No cards added
      </Text>

      <Button
        disabled={!isEligible}
        minW="300px"
        minH="64px"
        pos="relative"
        // top="100px"
        mt="80px !important"
        onClick={() => history.push("/dashboard/cards/request", { cards })}
      >
        Request Card
      </Button>
    </VStack>
  );
};

const UserCards = ({ isEligible, cards, setQueries }) => {
  const history = useHistory();
  const toast = useToast();

  const [idx, setIdx] = useState(-1);
  const [filter, setFilter] = useState("active");

  const [unfreezeCard, { data, isLoading, isError, error, isSuccess }] =
    useUnfreezeUserCardMutation();

  useEffect(() => {
    if (isSuccess && data) {
      toast({
        position: "bottom-right",
        title: "Success",
        description: "You've successfully unfreeze your card",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    }

    if (isError && error) {
      toast({
        position: "bottom-right",
        title: "Error",
        description: getError(error?.data?.error),
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  }, [isSuccess, error, data, isError, toast]);

  const handleFilter = (filter) => {
    setFilter(filter);
    if (filter === "active") setQueries({ active: true });
    if (filter === "all") setQueries({ active: false, disabled: false });
  };

  return (
    <Stack>
      <HStack justifyContent="space-between">
        <Select
          minW="100px"
          value={filter ?? ""}
          onChange={(e) => handleFilter(e.target.value)}
        >
          {["all", "active"].map((value) => (
            <option value={value} key={`status-${value}`}>
              {capitalize(value)}
            </option>
          ))}
        </Select>

        <Button
          disabled={!isEligible}
          variant="ghost"
          bg="transparent"
          size="sm"
          color="#03A438"
          onClick={() => history.push("/dashboard/cards/request", { cards })}
        >
          <HStack>
            <Box as={AddCircleIcon} />
            <Text>Request Card</Text>
          </HStack>
        </Button>
      </HStack>

      <HStack
        flexWrap="wrap"
        gridGap="20px"
        mt="32px !important"
        justifyContent="flex-start"
        alignItems="flex-start"
        w="100%"
      >
        {cards.map((option, i) => (
          <Card
            // showBalance
            onClick={
              ["active", "unfrozen"].includes(option?.status)
                ? () =>
                    history.push(
                      `/dashboard/cards/${option.cardId}?provider=${
                        option?.provider ?? "p1"
                      }&version=${option?.cardVersion ?? "v1"}`
                    )
                : undefined
            }
            colorScheme={getCardScheme(option?.cardType?.type)}
            // balance={+option?.balance?.$numberDecimal ?? 0}
            // currency="usd"
            validity={
              (option?.expiry ?? "").includes("undefined")
                ? null
                : option?.expiry
            }
            onUnfreeze={() => {
              setIdx(i);
              unfreezeCard(option.cardId);
            }}
            isUnfreezing={idx === i && isLoading}
            isFrozen={toLower(option?.status) === "frozen"}
            number={!!option?.cardNumber ? option?.cardNumber : null}
            name={option?.userDetails?.fullName}
            mask="*"
            status={option?.status}
            showStatus={
              !["active", "unfrozen", "frozen"].includes(option?.status)
            }
            brand={option?.brand}

            // {...option}
          />
        ))}
      </HStack>
    </Stack>
  );
};

export default function VirtualCards({
  cards,
  isEligible,
  isFetching,
  isLoading,
  userData,
  setQueries,
}) {
  const canRequestCard = useMemo(
    () => isEligible && toLower(userData?.country) === "nigeria",
    [isEligible, userData]
  );

  return (
    <>
      {cards?.length === 0 && (
        <EmptyState isEligible={canRequestCard} cards={cards} />
      )}

      {cards?.length > 0 && (
        <UserCards
          isEligible={canRequestCard}
          isLoading={isFetching}
          cards={cards}
          setQueries={setQueries}
        />
      )}

      <HStack w="100%" alignItems="center" justifyContent="center">
        {isLoading && (
          <CircularProgress
            disabled
            isIndeterminate
            color="#a7a7a7"
            size="32px"
          />
        )}
      </HStack>
    </>
  );
}
